/**
 * WSClient — single point of entry for all Y.js WebSocket connections.
 *
 * Why this exists
 * ───────────────
 * Today every part of the app constructs its own `WebsocketProvider` and
 * calls `wsTokenRefresher.register(...)` (sometimes), `wsReconnectCoordinator
 * .register(...)` (sometimes), `attachWsObservability(...)` (sometimes). The
 * inconsistency is the bug:
 *
 *   - `BookFormatterApplication.ts` constructs a provider but never registers
 *     it with `wsTokenRefresher` → no hard-stop on 1008, no size budget, no
 *     auth refresh on token rotation.
 *   - `AppBuilderApplication.ts` same story.
 *   - `FileEditorApplication.ts` registers with `wsReconnectCoordinator` but
 *     not `wsTokenRefresher` → no hard-stop.
 *
 * On top of that, React strict-mode double-invokes useEffect, and several
 * call sites construct a fresh provider on every effect run. Without
 * deduplication that's two providers per room in dev, two simultaneous WS
 * handshakes, two awareness streams.
 *
 * `WSClient` consolidates all of this:
 *
 *   1. ONE construction site for `new WebsocketProvider(...)`. Every safety
 *      attachment (hard-stop, size-budget, auth-refresh, observability) is
 *      wired here, exactly once per provider.
 *
 *   2. Refcount per `roomname`. Two parallel `open()` calls for the same
 *      room (typically: React strict-mode double useEffect, or two
 *      components both wanting the same notification stream) share ONE
 *      provider. The second `close()` (refcount → 0) tears it down.
 *
 *   3. `Handle` returned by `open()`. The handle is the proof-of-life
 *      ticket — `close()` releases the refcount. Idempotent.
 *
 * The kill-switch (`VITE_WS_CLIENT_ENABLED=false`) disables refcount + auto
 * observability; everything else (provider construction, registration with
 * the legacy `wsTokenRefresher` so hard-stop + size-budget still apply,
 * teardown on close) still works. This lets us roll out incrementally and
 * revert quickly if refcount semantics turn out to have a sharp edge.
 *
 * Phase 2D will fold `wsTokenRefresher` and `wsReconnectCoordinator` into
 * this module after the call-site migration completes.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import type { WebsocketProvider } from 'y-websocket';
import type * as Y from 'yjs';
import { HocuspocusWebsocketProvider } from './hocuspocusWebsocketProvider';
import { wsTokenRefresher } from '../wsTokenRefresher';

// The collaboration server is Hocuspocus, whose wire protocol is NOT compatible
// with y-websocket's WebsocketProvider. `HocuspocusWebsocketProvider` is an
// API-compatible adapter (same constructor signature + surface) backed by
// HocuspocusProvider. It is typed here as `typeof WebsocketProvider` because it
// exposes the identical surface the rest of this module + the safety stack rely
// on; the cast is the single boundary where the two providers are interchanged.
const DefaultProviderCtor =
	HocuspocusWebsocketProvider as unknown as typeof WebsocketProvider;
import {
	attachWsObservability,
	type WsObservabilityHandle,
	type WsObservabilityMetadata,
} from './wsObservability';

// NOTE: We intentionally do NOT import `WEBSOCKET_URL` / `getWsAuthParams`
// from `../../api/config` here. That module reads `window.location` at
// load time, which crashes the Node-only vitest environment used by the
// rest of `services/yjs/__tests__/`. The browser-side production
// defaults are wired up in `wsClientSingleton.ts`. Tests construct
// `WSClientImpl` directly with explicit `url` + `paramsProvider` deps.

// ─── Public types ─────────────────────────────────────────────────────────

export interface WSClientOpenOptions {
	/** Full room name, e.g. `'org:69bb...'` or `'doc:abc'` or `'broadcast:42'`. */
	roomname: string;
	/** Y.Doc to bind. Two `open()` calls with the same roomname MUST pass the
	 * SAME doc instance — passing a different doc throws `WSClientDocMismatchError`. */
	doc: Y.Doc;

	/** Optional explicit prefix for observability/limits. Auto-inferred from
	 * roomname (text before the first ':') if omitted. */
	prefix?: string;
	/** Optional auth0 sub for cross-referencing user-specific issues in Sentry. */
	authId?: string | null;

	/** Forwarded to WebsocketProvider. Default: true. */
	connect?: boolean;
	/** Forwarded to WebsocketProvider. Default: 30000. */
	resyncInterval?: number;
	/** Forwarded to WebsocketProvider. Default: 30000. Caps reconnect backoff. */
	maxBackoffTime?: number;

	/** When true (default), attach Sentry breadcrumbs + first-useful-message
	 * span to the provider. When false, skip even for prefixes that would
	 * normally get observability. The kill-switch disables this globally. */
	attachObservability?: boolean;

	/** When true, bypass the per-roomname refcount and build a dedicated
	 * provider for this caller. Use this for one-shot reads with a
	 * temporary Y.Doc that intentionally must NOT join the shared session
	 * (e.g. `BookFormatterApplication.fetchContentFromWebSocket` reads
	 * `doc:${docId}` into a throwaway tempDoc while the user may have the
	 * same doc open with a separate Y.Doc in DocsApp).
	 *
	 * Defaults to false. Observability auto-attach is suppressed in this
	 * mode (mirrors the kill-switch behavior) — pass `attachObservability:
	 * true` if you want breadcrumbs for the dedicated provider too. */
	exclusive?: boolean;

	/** Test seam: override the URL the provider connects to. */
	urlOverride?: string;
	/** Test seam: override `getWsAuthParams()` output. */
	paramsOverride?: Record<string, string>;
}

export interface WSClientHandle {
	readonly roomname: string;
	readonly provider: WebsocketProvider;
	/** Awareness instance from the underlying provider. */
	readonly awareness: WebsocketProvider['awareness'];
	/** Releases this handle's refcount. Idempotent. When the LAST handle
	 * closes (refcount → 0), the provider is fully torn down: unregistered
	 * from the token refresher, observability disposed, disconnected, and
	 * destroyed. */
	close(): void;
	/** True after `close()` has been called on this handle. */
	isClosed(): boolean;
}

export class WSClientDocMismatchError extends Error {
	constructor(roomname: string) {
		super(`WSClient.open(${roomname}): a different Y.Doc is already bound to this room. Each room must be opened with the same Y.Doc instance for refcounting to be safe.`);
		this.name = 'WSClientDocMismatchError';
	}
}

// ─── Internals ───────────────────────────────────────────────────────────

interface ProviderEntry {
	provider: WebsocketProvider;
	doc: Y.Doc;
	prefix: string;
	refcount: number;
	observability: WsObservabilityHandle | null;
	/** Set once the provider has been torn down. Guards against double-dispose
	 * when more than one path reaches teardown for the same entry — e.g.
	 * `evictStaleEntry()` disposing an orphaned entry and a later `close()` on a
	 * handle that still references it. */
	disposed: boolean;
}

// Auto-attach observability for prefixes whose connection latency / failure
// modes are tracked in incident dashboards. Adding a prefix here costs one
// extra Sentry breadcrumb stream per active provider — opt-in deliberately.
const AUTO_OBSERVABILITY_PREFIXES = new Set([
	'org',
	'org-mine',
	'notification',
	'conversation',
	'doc',
]);

function inferPrefix(roomname: string): string {
	const colon = roomname.indexOf(':');
	return colon === -1 ? roomname : roomname.slice(0, colon);
}

function readKillSwitch(): boolean {
	// Default ON. Only literal 'false' disables — anything else (unset,
	// 'true', empty) keeps refcount + observability on.
	try {
		// `import.meta.env` is replaced at build time. In test/node contexts
		// where it's undefined we treat the switch as ON (the default).
		const value = (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
			.env?.VITE_WS_CLIENT_ENABLED;
		return value !== 'false';
	} catch {
		return true;
	}
}

// ─── Dependencies (test seam) ────────────────────────────────────────────

export interface WSClientDeps {
	/** WebsocketProvider constructor. Defaults to the y-websocket export. */
	ProviderCtor?: typeof WebsocketProvider;
	/** Token refresher with `register` / `unregister`. Defaults to the
	 * shared singleton. */
	tokenRefresher?: {
		register: (provider: WebsocketProvider) => void;
		unregister: (provider: WebsocketProvider) => void;
	};
	/** Factory for observability handles. Defaults to `attachWsObservability`. */
	observabilityFactory?: (
		provider: WebsocketProvider,
		metadata: WsObservabilityMetadata,
	) => WsObservabilityHandle;
	/** WebSocket URL (defaults to `WEBSOCKET_URL`). */
	url?: string;
	/** Auth params provider (defaults to `getWsAuthParams`). */
	paramsProvider?: () => Record<string, string>;
	/** When supplied, forces the kill-switch state regardless of env. */
	killSwitchOverride?: boolean;
	/** Optional logger; defaults to `console`. */
	logger?: { warn: (...args: unknown[]) => void; error: (...args: unknown[]) => void };
}

// ─── Implementation ──────────────────────────────────────────────────────

export class WSClientImpl {
	private readonly entries = new Map<string, ProviderEntry>();
	private readonly ProviderCtor: typeof WebsocketProvider;
	private readonly tokenRefresher: NonNullable<WSClientDeps['tokenRefresher']>;
	private readonly observabilityFactory: NonNullable<WSClientDeps['observabilityFactory']>;
	private readonly url: string;
	private readonly paramsProvider: () => Record<string, string>;
	private readonly enabled: boolean;
	private readonly logger: NonNullable<WSClientDeps['logger']>;

	constructor(deps: WSClientDeps = {}) {
		this.ProviderCtor = deps.ProviderCtor ?? DefaultProviderCtor;
		this.tokenRefresher = deps.tokenRefresher ?? wsTokenRefresher;
		this.observabilityFactory = deps.observabilityFactory ?? attachWsObservability;
		this.url = deps.url ?? '';
		this.paramsProvider = deps.paramsProvider ?? (() => ({}));
		this.enabled = deps.killSwitchOverride ?? readKillSwitch();
		this.logger = deps.logger ?? console;
	}

	open(opts: WSClientOpenOptions): WSClientHandle {
		if (!opts || typeof opts !== 'object') {
			throw new TypeError('WSClient.open: options object is required');
		}
		if (!opts.roomname || typeof opts.roomname !== 'string') {
			throw new TypeError('WSClient.open: roomname must be a non-empty string');
		}
		if (!opts.doc || typeof (opts.doc as { on?: unknown }).on !== 'function') {
			throw new TypeError('WSClient.open: doc must be a Y.Doc');
		}

		// Kill-switch path: every open() builds a fresh provider with no
		// refcount / no observability. Hard-stop and size-budget still apply
		// because we route through wsTokenRefresher.register.
		if (!this.enabled) {
			return this.openWithoutRefcount(opts);
		}

		// Exclusive mode: caller wants a dedicated provider, NOT a refcount
		// share. Same teardown semantics as the kill-switch path. Used by
		// short-lived one-shot reads with a throwaway Y.Doc.
		if (opts.exclusive) {
			return this.openWithoutRefcount(opts);
		}

		const existing = this.entries.get(opts.roomname);
		if (existing) {
			if (existing.doc !== opts.doc) {
				throw new WSClientDocMismatchError(opts.roomname);
			}
			existing.refcount++;
			return this.makeHandle(opts.roomname, existing);
		}

		const entry = this.constructEntry(opts);
		this.entries.set(opts.roomname, entry);
		return this.makeHandle(opts.roomname, entry);
	}

	/** Snapshot of current state — used by /debug surfaces and tests. */
	getDebugState(): {
		enabled: boolean;
		roomCount: number;
		rooms: Array<{ roomname: string; refcount: number; prefix: string }>;
	} {
		return {
			enabled: this.enabled,
			roomCount: this.entries.size,
			rooms: Array.from(this.entries.entries()).map(([roomname, entry]) => ({
				roomname,
				refcount: entry.refcount,
				prefix: entry.prefix,
			})),
		};
	}

	// ─── Private ─────────────────────────────────────────────────────────

	private constructEntry(opts: WSClientOpenOptions): ProviderEntry {
		const prefix = opts.prefix ?? inferPrefix(opts.roomname);
		const provider = new this.ProviderCtor(
			opts.urlOverride ?? this.url,
			opts.roomname,
			opts.doc as never,
			{
				connect: opts.connect ?? true,
				params: opts.paramsOverride ?? this.paramsProvider(),
				resyncInterval: opts.resyncInterval ?? 30_000,
				maxBackoffTime: opts.maxBackoffTime ?? 30_000,
			},
		);

		// `wsTokenRefresher.register` is the canonical entry point during the
		// transition — it attaches hard-stop + size-budget internally and
		// registers with `wsReconnectCoordinator`. Doing it here means every
		// provider built by WSClient gets the full safety stack with no
		// per-call-site code.
		try {
			this.tokenRefresher.register(provider);
		} catch (err) {
			// Refusing to attach hard-stop is bad but not fatal — log and continue
			// so the room still works (degraded). The hard-stop integration test
			// would catch a real regression here.
			this.logger.warn('[WSClient] tokenRefresher.register failed:', err);
		}

		let observability: WsObservabilityHandle | null = null;
		const wantObservability = opts.attachObservability ?? AUTO_OBSERVABILITY_PREFIXES.has(prefix);
		if (wantObservability) {
			try {
				observability = this.observabilityFactory(provider, {
					room: opts.roomname,
					prefix,
					authId: opts.authId ?? null,
				});
			} catch (err) {
				this.logger.warn('[WSClient] observability attach failed:', err);
			}
		}

		return {
			provider,
			doc: opts.doc,
			prefix,
			refcount: 1,
			observability,
			disposed: false,
		};
	}

	private makeHandle(roomname: string, entry: ProviderEntry): WSClientHandle {
		let closed = false;
		const close = () => {
			if (closed) return;
			closed = true;
			this.releaseEntry(roomname, entry);
		};
		return {
			roomname,
			provider: entry.provider,
			awareness: entry.provider.awareness,
			close,
			isClosed: () => closed,
		};
	}

	private releaseEntry(roomname: string, entry: ProviderEntry): void {
		entry.refcount--;
		if (entry.refcount > 0) return;

		// Double-check the entry we're tearing down is still the registered
		// one. If it isn't (because a concurrent open after our release
		// installed a fresh entry), leave the new one alone.
		const current = this.entries.get(roomname);
		if (current === entry) {
			this.entries.delete(roomname);
		}
		this.disposeEntry(entry);
	}

	private disposeEntry(entry: ProviderEntry): void {
		if (entry.disposed) return;
		entry.disposed = true;
		try { entry.observability?.dispose(); } catch (err) {
			this.logger.warn('[WSClient] observability dispose failed:', err);
		}
		try { this.tokenRefresher.unregister(entry.provider); } catch (err) {
			this.logger.warn('[WSClient] tokenRefresher.unregister failed:', err);
		}
		try { entry.provider.disconnect(); } catch { /* best-effort */ }
		try { entry.provider.destroy(); } catch (err) {
			this.logger.warn('[WSClient] provider.destroy failed:', err);
		}
	}

	private openWithoutRefcount(opts: WSClientOpenOptions): WSClientHandle {
		// Kill-switch fallback. Mirrors today's call-site behavior except
		// that registration with the token refresher is GUARANTEED — that's
		// the bug we're fixing.
		//
		// Observability auto-attach is suppressed here unless the caller
		// explicitly opts in (`attachObservability: true`). The kill-switch
		// is the "minimal-surface" mode — only the safety attachments that
		// existed before WSClient remain active.
		const entry = this.constructEntry({
			...opts,
			attachObservability: opts.attachObservability ?? false,
		});
		// Detach the entry from the refcount map so subsequent opens of the
		// same roomname construct fresh providers (today's behavior).
		// We still build a single-use handle whose close() tears down THIS
		// provider only.
		let closed = false;
		const close = () => {
			if (closed) return;
			closed = true;
			this.disposeEntry(entry);
		};
		return {
			roomname: opts.roomname,
			provider: entry.provider,
			awareness: entry.provider.awareness,
			close,
			isClosed: () => closed,
		};
	}

	/**
	 * If there is an existing entry for `roomname` whose Y.Doc differs from
	 * `doc`, force-evict it (dispose the provider, remove the entry) regardless
	 * of refcount.
	 *
	 * This handles the Vite HMR case: the `wsClient` singleton survives module
	 * re-execution (it lives on `globalThis`), but a store's module-level
	 * `_wsHandle` variable is reset to `null` on reload. Without eviction,
	 * the next `open({ doc: newDoc })` would throw `WSClientDocMismatchError`
	 * because the old entry (refcount still 1, old Y.Doc) is still registered.
	 *
	 * CONTRACT: only call this from the SINGLE exclusive owner of `roomname`
	 * (e.g. the module-level singleton stores `orgYDocStore` /
	 * `notificationsYjsStore`, which open exactly one handle to their room).
	 * The eviction disposes the provider even if other handles still reference
	 * it — those handles' `close()` stay safe (they no-op once the entry is
	 * gone) but their `.provider` becomes a destroyed instance. Do NOT use this
	 * on refcount-shared rooms (strict-mode double-effects, multi-subscriber
	 * notification streams, etc.) where peers legitimately share one provider.
	 *
	 * No-op when there is no entry for `roomname` or the existing entry already
	 * uses the same Y.Doc instance.
	 */
	evictStaleEntry(roomname: string, doc: Y.Doc): void {
		const entry = this.entries.get(roomname);
		if (!entry || entry.doc === doc) return;
		this.entries.delete(roomname);
		this.disposeEntry(entry);
	}

	/** Test-only — drops every entry and tears down the provider. */
	_resetForTests(): void {
		for (const [, entry] of this.entries) {
			this.disposeEntry(entry);
		}
		this.entries.clear();
	}
}
